import React, { useEffect, useRef, useState } from "react";

import "./news.less";

import priceBg from "@assets/images/official_website_new/price_bg.jpg";
import noDataIcon from "@assets/images/no_data_new_light.svg";
import errorIcon from "@assets/images/load_fail.svg";
import NewsCard from "@components/news_card";
import Iconfont from "@components/iconfont/iconfont";
import { formatNewsPublishDate } from "../../shared/news";
import useNewsList from "../../shared/useNewsList";
import BackTopButton from "../../shared/BackTopButton";
import { scrollPageToTop } from "../../shared/scrollTop";

/** 首屏骨架卡数量：一行一张，约一屏半 */
const NEWS_SKELETON_COUNT = 3;

/** 续页骨架兜底数量：接口未返回 total 时使用 */
const NEWS_MORE_SKELETON_FALLBACK = 3;

/** 继续下拉多远（视觉位移，px）才触发下一页 */
const PULL_THRESHOLD = 60;

/** 下拉阻尼：手指位移 × 系数 = 视觉位移 */
const PULL_RESISTANCE = 0.5;

/** 最大下拉视觉位移 */
const PULL_MAX = 90;

/** 滚轮停止多久后结算一次下拉 */
const WHEEL_SETTLE_DELAY = 160;

/** Tab 行吸顶时距视口顶部的位置：等于固定 nav（56）的高度 */
const STICKY_TOP = 56;

/**
 * 资讯页 / Mobile —— 与 Web + Pad 的 NewsPage 分开维护，
 * 由 mobile/App.jsx 在 `view === "news"` 时渲染。
 *
 * 结构：顶部 Banner（2656:9059）→ 分类 Tab（2656:9076）→ 资讯列表；
 * 整页走文档滚动：Banner 先滚走，Tab 行吸顶在 nav 下方，列表继续往下排；
 * 列表取数 / 分页 / 错误态 / 空态与 Web 共用 shared/useNewsList，
 * 两端只有「怎么触发续页」不同：这里用下拉阈值手势，Web 用底部哨兵。
 * Banner 背景图与 Web / Pad 共用 price_bg.jpg；分类与格式化逻辑在 shared/news。
 */
export default function MobileNewsPage({ onOpenDetail, onFooterHiddenChange }) {
  /* 分类 Tab 行：判断是否已吸顶 */
  const tabsRef = useRef(null);
  /* 列表内容：下拉时整体上移，松手回弹 */
  const listContentRef = useRef(null);
  /* 下拉 / 滚轮手势状态 */
  const pullRef = useRef({
    tracking: false,
    pulling: false,
    startY: 0,
    distance: 0,
    wheelDistance: 0,
    wheelTimer: 0,
  });

  const {
    tabs,
    activeCategory,
    articles,
    listLoading,
    loadingMore,
    hasMore,
    hasError,
    showPinned,
    showEmpty,
    moreSkeletonCount,
    loadMore,
    changeCategory,
    retry,
  } = useNewsList({ moreSkeletonFallback: NEWS_MORE_SKELETON_FALLBACK });

  /**
   * 点击分类 Tab：切换分类后把页面滚动条拉回顶部。
   *
   * 以前这里是回到「Tab 行刚吸顶」的位置（pinTop），只保证新列表从第一条开始，
   * Banner 仍滚在视口外；现在按需求统一回到页面顶部（与 Web / Pad 一致），
   * 点了哪个 Tab 都置顶 —— 包括点当前已选中的那个（用户常把它当「回顶部」用）。
   * 用 scrollPageToTop 而不是 window.scrollTo：切换后正文先变骨架再变高，
   * iOS Safari 的平滑滚动中途被打断会停在半路。
   */
  const handleTabClick = (value) => {
    changeCategory(value);
    scrollPageToTop();
  };

  /* 列表到底提示（已加载全部文章 + 返回顶部）是否进入视口：
     悬浮回到顶部按钮由 BackTopButton 负责「滚动后出现」，这里只负责「提示出现就让位」 */
  const [listEndVisible, setListEndVisible] = useState(false);
  /* Tab 行是否已吸顶：吸顶时补一条 1px 分隔线 */
  const [tabsStuck, setTabsStuck] = useState(false);
  /* 列表到底提示节点：只有 !hasMore 时才渲染 */
  const listEndRef = useRef(null);

  useEffect(() => {
    let raf = 0;

    const update = () => {
      raf = 0;
      const tabsEl = tabsRef.current;
      /* 吸顶时 top 正好等于 STICKY_TOP，留 0.5 容差避免亚像素抖动 */
      setTabsStuck(
        Boolean(tabsEl) &&
          tabsEl.getBoundingClientRect().top <= STICKY_TOP + 0.5
      );

      /* 到底提示进入视口（top 越过视口底部）即视为「已出现」 */
      const endEl = listEndRef.current;
      setListEndVisible(
        endEl ? endEl.getBoundingClientRect().top < window.innerHeight : false
      );
    };

    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [hasMore, listLoading, loadingMore, articles.length, activeCategory]);

  /* 还有更多、或首屏 / 续页还在加载时，先让 App 别渲染 footer：
     文档底部就等于列表底部，下拉手势才能稳定接管「继续下拉加载下一页」 */
  const footerHidden = hasMore || listLoading || loadingMore;
  useEffect(() => {
    if (onFooterHiddenChange) onFooterHiddenChange(footerHidden);
  }, [footerHidden, onFooterHiddenChange]);

  /**
   * 触底加载更多：整页滚动（Banner 先滚走、Tab 行吸顶），
   * 滚到文档底部后继续向下拉一段距离（阈值 PULL_THRESHOLD）才请求下一页。
   * - 判据用文档滚动量：还有更多时 App 不渲染 footer，文档底部即列表底部；
   * - 列表内容整体上移，形成「回弹」的宽容量；
   * - 松手时达到阈值才加载，没达到则回弹且不请求；
   * - 同时支持触摸（移动端）与滚轮（桌面调试）。
   */
  useEffect(() => {
    if (!hasMore || listLoading || loadingMore || hasError) return undefined;

    const pull = pullRef.current;
    const applyPull = (distance, animate) => {
      const content = listContentRef.current;
      if (!content) return;
      content.style.transition = animate ? "transform 0.2s ease-out" : "none";
      content.style.transform =
        distance > 0 ? `translateY(-${distance}px)` : "";
    };

    const atBottom = () => {
      const doc = document.documentElement;
      return window.scrollY + window.innerHeight >= doc.scrollHeight - 1;
    };

    const clearWheelTimer = () => {
      if (pull.wheelTimer) {
        clearTimeout(pull.wheelTimer);
        pull.wheelTimer = 0;
      }
    };

    const triggerLoad = () => {
      loadMore();
    };

    const settlePull = (allowLoad) => {
      pull.tracking = false;
      pull.wheelDistance = 0;
      if (!pull.pulling) return;
      pull.pulling = false;
      const reached = pull.distance >= PULL_THRESHOLD;
      pull.distance = 0;
      applyPull(0, true);
      if (reached && allowLoad) triggerLoad();
    };

    const onTouchStart = (event) => {
      if (event.touches.length !== 1) return;
      /* Tab 行是横向滚动区域：从它上面开始的手势整段不参与下拉，
         否则横向滑动 Tab 时带一点点竖直位移就会被拦成下拉 */
      const target = event.target;
      if (
        target &&
        typeof target.closest === "function" &&
        target.closest(".m-news-tabs")
      ) {
        return;
      }
      clearWheelTimer();
      pull.tracking = true;
      pull.pulling = false;
      pull.wheelDistance = 0;
      pull.startY = event.touches[0].clientY;
      pull.distance = 0;
      applyPull(0, false);
    };

    const onTouchMove = (event) => {
      if (!pull.tracking) return;
      const currentY = event.touches[0].clientY;

      /* 还没到底：先让列表正常滚，到底后再从当前位置接上下拉 */
      if (!pull.pulling) {
        if (!atBottom()) {
          pull.startY = currentY;
          return;
        }
        if (pull.startY - currentY <= 0) {
          pull.startY = currentY;
          return;
        }
        pull.pulling = true;
      }

      const delta = pull.startY - currentY;
      if (delta <= 0) {
        pull.distance = 0;
        applyPull(0, false);
        return;
      }
      event.preventDefault();
      const distance = Math.min(PULL_MAX, delta * PULL_RESISTANCE);
      pull.distance = distance;
      applyPull(distance, false);
    };

    const onTouchEnd = () => settlePull(true);
    const onTouchCancel = () => settlePull(false);

    const onWheel = (event) => {
      if (event.deltaY <= 0 || !atBottom()) return;
      event.preventDefault();
      clearWheelTimer();
      const distance = Math.min(
        PULL_MAX,
        pull.wheelDistance + event.deltaY * PULL_RESISTANCE
      );
      pull.wheelDistance = distance;
      applyPull(distance, false);
      pull.wheelTimer = setTimeout(() => {
        const reached = pull.wheelDistance >= PULL_THRESHOLD;
        pull.wheelDistance = 0;
        pull.wheelTimer = 0;
        applyPull(0, true);
        if (reached) triggerLoad();
      }, WHEEL_SETTLE_DELAY);
    };

    /* 整页滚动：手势挂在 window 上，列表不再是独立的滚动容器 */
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchCancel);
    window.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchCancel);
      window.removeEventListener("wheel", onWheel);
      clearWheelTimer();
      pull.tracking = false;
      pull.pulling = false;
      pull.distance = 0;
      pull.wheelDistance = 0;
      applyPull(0, false);
    };
  }, [hasMore, listLoading, loadingMore, hasError, loadMore]);

  /** 回到顶部：整页滚动，直接回到文档顶部（Banner 顶部） */
  const handleBackToTop = () => {
    scrollPageToTop();
  };

  return (
    <div className="m-news-page">
      <section
        className="m-news-hero"
        style={{ backgroundImage: `url("${priceBg}")` }}
      >
        <h1 className="m-news-hero__title">品牌数字化建设，从此刻开始</h1>
        <p className="m-news-hero__sub">更多个性建站技巧、私域营销干货，持续更新中</p>
      </section>

      {/* 空态也保留 Tab 行（与 Web 一致），否则某一分类为空时用户无法切回去 */}
      {!hasError && tabs.length > 0 ? (
        <div
          ref={tabsRef}
          className={`m-news-tabs${tabsStuck ? " is-stuck" : ""}`}
          role="group"
          aria-label="资讯分类"
        >
          {tabs.map((tab) => {
            const isActive = tab.value === activeCategory;

            return (
              <button
                key={tab.value}
                type="button"
                className={`m-news-tab${isActive ? " is-active" : ""}`}
                aria-pressed={isActive}
                onClick={() => handleTabClick(tab.value)}
              >
                <span className="m-news-tab__label">{tab.label}</span>
                <span className="m-news-tab__line" aria-hidden="true" />
              </button>
            );
          })}
        </div>
      ) : null}

      {hasError ? (
        <div className="m-news-state-wrap">
          <div className="m-news-state" role="alert">
            <img className="m-news-state__icon" src={errorIcon} alt="" />
            <p className="m-news-state__title">内容加载失败</p>
            <p className="m-news-state__desc">请检查网络后重试</p>
            <button
              type="button"
              className="m-news-state__retry"
              onClick={retry}
            >
              重新加载
            </button>
          </div>
        </div>
      ) : showEmpty ? (
        <div className="m-news-state-wrap">
          <div className="m-news-state" role="status">
            <img className="m-news-state__icon" src={noDataIcon} alt="" />
            <p className="m-news-state__empty">暂无数据</p>
          </div>
        </div>
      ) : (
        <div className="m-news-list-wrap">
          <div
            className="m-news-list-section"
            aria-busy={listLoading || loadingMore}
          >
          <div className="m-news-list-body" ref={listContentRef}>
            <div className="m-news-list">
              {listLoading
                ? Array.from({ length: NEWS_SKELETON_COUNT }, (_, index) => (
                    <NewsCard key={`m-news-skeleton-${index}`} skeleton />
                  ))
                : articles.map((article) => (
                    <NewsCard
                      key={article.id}
                      brandName="BUJUE"
                      typeName={article.categoryLabel || ""}
                      title={article.title || ""}
                      coverUrl={article.cover || ""}
                      pinned={showPinned && Boolean(article.pinned)}
                      category={article.categoryLabel || ""}
                      summary={article.summary || ""}
                      teamName={article.author || ""}
                      publishDate={formatNewsPublishDate(article.tmPublished)}
                      onClick={() =>
                        onOpenDetail?.(article.id, article.title)
                      }
                    />
                  ))}

              {!listLoading && loadingMore
                ? Array.from({ length: moreSkeletonCount }, (_, index) => (
                    <NewsCard key={`m-news-more-skeleton-${index}`} skeleton />
                  ))
                : null}
            </div>

            {!listLoading && hasMore && !loadingMore ? (
              <div className="m-news-load-more">
                <Iconfont
                  type="icon-a-jiantouxiangxiaxi"
                  className="m-news-load-more__icon"
                />
                <p className="m-news-load-more__text">
                  继续向下滑动，加载更多文章
                </p>
              </div>
            ) : null}

            {!listLoading && !loadingMore && !hasMore ? (
              <div className="m-news-list-end" ref={listEndRef}>
                <p className="m-news-list-end__text">已加载全部文章</p>
                <button
                  type="button"
                  className="m-news-list-end__back"
                  onClick={handleBackToTop}
                >
                  <Iconfont
                    type="icon-a-jiantouxiangshangxi"
                    className="m-news-list-end__icon"
                  />
                  <span>返回顶部</span>
                </button>
              </div>
            ) : null}
          </div>
          </div>

          {/* 滚动后出现；到底提示（已加载全部文章 + 返回顶部）出现后让位 */}
          <BackTopButton hidden={listEndVisible} />
        </div>
      )}
    </div>
  );
}
